import './theme-drawer.js';
import './stark-cart-trigger.js';
import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

// Keep the cart drawer attached to the viewport. A later custom loading style
// sets the dialog to position: relative; this stronger rule prevents the cart
// from rendering at the top of the document on iOS/Safari.
const CART_DRAWER_POSITION_FIX_ID = 'stark-cart-drawer-position-fix';
if (!document.getElementById(CART_DRAWER_POSITION_FIX_ID)) {
  const style = document.createElement('style');
  style.id = CART_DRAWER_POSITION_FIX_ID;
  style.textContent = `
    #cart-drawer .theme-drawer__dialog {
      position: fixed !important;
      top: 0 !important;
      bottom: auto !important;
      height: 100dvh !important;
    }

    /* Safari on iPhone zooms when focusing inputs below 16px. */
    @media screen and (max-width: 990px) {
      #cart-drawer input[data-tax-rut],
      #cart-drawer .tax-document-selector__rut input,
      #cart-drawer input[name='attributes[RUT]'],
      #cart-drawer .stark-rut-input {
        font-size: 16px !important;
        line-height: 1.35;
      }
    }

    .stark-mobile-dock,
    .stark-mobile-dock button[aria-controls="cart-drawer"] {
      overflow: visible !important;
    }

    .stark-mobile-dock button[aria-controls="cart-drawer"] {
      position: relative;
    }

    .stark-mobile-dock__cart-badge {
      position: absolute;
      top: 2px;
      left: calc(50% + 5px);
      z-index: 3;
      display: none;
      min-width: 11px;
      height: 11px;
      padding: 0 2px;
      box-sizing: border-box;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      background: #ff7900;
      color: #fff;
      font-family: inherit;
      font-size: 6px !important;
      font-weight: 600;
      font-style: normal;
      line-height: 1;
      letter-spacing: 0;
      text-align: center;
      white-space: nowrap;
      box-shadow: 0 0 0 1px #fff;
      pointer-events: none;
    }

    .stark-mobile-dock__cart-badge[data-visible='true'] {
      display: inline-flex;
    }
  `;
  document.head.append(style);
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
    this.renderMobileDockBadge(this.currentCartCount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = (event) => {
    if (event.persisted) this.ensureCartBubbleIsCorrect();
    this.renderMobileDockBadge(this.currentCartCount);
  };

  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  getMobileDockBadge = () => {
    const button = document.querySelector('.stark-mobile-dock button[aria-controls="cart-drawer"]');
    if (!button) return null;

    let badge = button.querySelector('.stark-mobile-dock__cart-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'stark-mobile-dock__cart-badge';
      badge.setAttribute('aria-hidden', 'true');
      button.append(badge);
    }

    return badge;
  };

  renderMobileDockBadge = (itemCount) => {
    const badge = this.getMobileDockBadge();
    if (!badge) return;

    badge.textContent = itemCount > 99 ? '99+' : String(itemCount);
    badge.dataset.visible = itemCount > 0 ? 'true' : 'false';
  };

  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.currentCartCount = itemCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    this.renderMobileDockBadge(itemCount);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
    );

    if (!animate || itemCount === 0) return;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;
    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      if (value === visibleCount) {
        this.renderMobileDockBadge(parseInt(value, 10) || 0);
        return;
      }
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (count >= 0) this.renderCartBubble(count, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
